import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import {
  AreaBreakdownData,
  AreaBreakdownItem,
  AreaBreakdownItemValue,
  AreaBreakdownKpi,
  useStoreInformation,
  useStoreOpeningHours,
  useTransaction,
  useUser,
} from '../../../hooks';
import { StorePerformanceInterval, createStorePerformanceRequest } from '../../../constants';
import { muiTheme } from '../../../theme';
import { ColumnFlexContainer, RowFlexContainer } from '../../../stories/atoms/containerV2/ContainerV2';
import { Chip, Icons, Typography } from '../../../stories/atoms';
import { salesAnalysisTranslation } from '../../../translations/salesAnalysis';
import { Animations, Animation, Button } from '../../../stories/molecules';
import { commonTranslations, homeTranslations } from '../../../translations';
import { Table } from '../../../stories/molecules/tablev2/Table';
import {
  formatAmountWithSeparator,
  formatValue,
  getDatePickerLabel,
  getIsWithinOperationHours,
  isFailureResponse,
} from '../../../utils';
import { areaResultTranslations } from '../../../translations/areaResult';

const getTimeUnitText = (interval: StorePerformanceInterval, timeUnit: string) => {
  if (interval === StorePerformanceInterval.DAILY) return `${timeUnit}:00`;

  return timeUnit;
};

const getFilteredValues = (
  areaBreakdownItemValues: AreaBreakdownItemValue[],
  storeOpeningHour: number,
  storeClosingHour: number,
): AreaBreakdownItemValue[] => {
  return areaBreakdownItemValues.filter((value) => {
    return getIsWithinOperationHours(value.timeUnit, storeOpeningHour, storeClosingHour);
  });
};
const areaBreakdownKpis: AreaBreakdownKpi[] = [
  'netSales',
  'conversionRate',
  'queueLength',
  'shareOfReceipts',
  'unrecruited',
];

type KpiState = {
  isLoading: boolean;
  isError: boolean;
  data: AreaBreakdownData | null;
};

const getDefaultKpiState = (): KpiState => {
  return {
    isLoading: false,
    isError: false,
    data: null,
  };
};

export type BreakdownViewProps = {
  interval: StorePerformanceInterval;
  currentDate: Date;
  targetCurrency: string;
  backButtonHandler: () => void;
};

export const BreakdownView: FC<BreakdownViewProps> = observer(
  ({ interval, currentDate, targetCurrency, backButtonHandler }) => {
    const { t } = useTranslation();
    const isTablet = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
    const {
      get: { storesDetails },
    } = useUser();
    const { fetchAreaBreakdownData } = useTransaction();
    const { areaName } = useStoreInformation();
    const { getStoreOpeningHours } = useStoreOpeningHours();
    const { storeOpeningHour, storeClosingHour } = getStoreOpeningHours(currentDate);

    const [normalizedStoreOpeningHour, setNormalizedStoreOpeningHour] = useState(storeOpeningHour);
    const [normalizedStoreClosingHour, setNormalizedStoreClosingHour] = useState(storeClosingHour);

    const dateLabel = getDatePickerLabel(
      currentDate,
      interval,
      t(homeTranslations.datePicker.weekLabel.key, homeTranslations.datePicker.weekLabel.defaultValue),
    );

    const [activeKpi, setActiveKpi] = useState<AreaBreakdownKpi>('netSales');

    const [kpiStates, setKpiStates] = useState<Record<AreaBreakdownKpi, KpiState>>({
      conversionRate: getDefaultKpiState(),
      netSales: getDefaultKpiState(),
      queueLength: getDefaultKpiState(),
      shareOfReceipts: getDefaultKpiState(),
      unrecruited: getDefaultKpiState(),
    });

    const getKpiName = (areaBreakdownKpi: AreaBreakdownKpi): string => {
      switch (areaBreakdownKpi) {
        case 'netSales':
          return t(
            areaResultTranslations.breakdownView.kpiNetSalesName.key,
            areaResultTranslations.breakdownView.kpiNetSalesName.defaultValue,
          );
        case 'conversionRate':
          return t(
            areaResultTranslations.breakdownView.kpiConversionRateName.key,
            areaResultTranslations.breakdownView.kpiConversionRateName.defaultValue,
          );
        case 'queueLength':
          return t(
            areaResultTranslations.breakdownView.kpiQueueLengthName.key,
            areaResultTranslations.breakdownView.kpiQueueLengthName.defaultValue,
          );
        case 'shareOfReceipts':
          return t(
            areaResultTranslations.breakdownView.kpiShareOfReceiptsName.key,
            areaResultTranslations.breakdownView.kpiShareOfReceiptsName.defaultValue,
          );
        case 'unrecruited':
          return t(
            areaResultTranslations.breakdownView.kpiUnrecruitedName.key,
            areaResultTranslations.breakdownView.kpiUnrecruitedName.defaultValue,
          );
        default:
          return '';
      }
    };

    const getKpiAmountText = (kpi: AreaBreakdownKpi, amount: number): string => {
      switch (kpi) {
        case 'netSales': {
          return formatValue(amount);
        }

        case 'conversionRate':
        case 'shareOfReceipts':
        case 'queueLength': {
          return formatAmountWithSeparator(amount, '%');
        }

        default:
          return amount.toString();
      }
    };

    const getKpiState = useCallback(
      (kpi: AreaBreakdownKpi): KpiState => {
        return kpiStates[kpi];
      },
      [kpiStates],
    );

    const getAreaBreakdownData = useCallback(async () => {
      setKpiStates((prev) => {
        return { ...prev, [activeKpi]: { isLoading: true, isError: false, data: null } };
      });

      const request = createStorePerformanceRequest({
        interval,
        currentDate,
      });

      const response = await fetchAreaBreakdownData({
        ...request,
        kpi: activeKpi,
        targetCurrency: targetCurrency,
      });

      if (isFailureResponse(response)) {
        setKpiStates((prev) => {
          return { ...prev, [activeKpi]: { isLoading: false, isError: true, data: null } };
        });

        return;
      }

      setKpiStates((prev) => {
        return { ...prev, [activeKpi]: { isLoading: false, isError: false, data: response.data } };
      });
    }, [currentDate, targetCurrency, activeKpi]);

    useEffect(() => {
      const { isLoading, data } = getKpiState(activeKpi);

      if (isLoading || !!data) return;

      getAreaBreakdownData();
    }, [currentDate, activeKpi]);

    const filteredStoresByArea = storesDetails.filter((store) => store.area === areaName);

    const activeKpiState = getKpiState(activeKpi);

    const { isLoading, isError, data } = activeKpiState;

    const breakdownItems: AreaBreakdownItem[] = data && data.breakdown ? data.breakdown : [];

    const normalizedBreakdownItems: AreaBreakdownItem[] = useMemo(() => {
      if (interval != StorePerformanceInterval.DAILY) return breakdownItems;

      return breakdownItems.map((breakdownItem) => {
        const { values } = breakdownItem;

        const valuesMap: { [key: string]: AreaBreakdownItemValue | undefined } = values.reduce((ac, curr) => {
          return { ...ac, [curr.timeUnit]: curr };
        }, {});

        const hourList = [...Array(24).keys()];

        let updatedNormalizedStoreOpeningHour = normalizedStoreOpeningHour;
        let updatedNormalizedStoreClosingHour = normalizedStoreClosingHour;

        const normalizedItems: AreaBreakdownItemValue[] = hourList.map((hour) => {
          const value = valuesMap[hour.toString()];

          if (!value) return { timeUnit: hour.toString(), amount: 0 };

          const { timeUnit, amount } = value;

          if (activeKpi !== 'conversionRate' && amount !== 0 && Number.parseInt(timeUnit) < storeOpeningHour) {
            updatedNormalizedStoreOpeningHour = Number.parseInt(timeUnit);
          }

          if (activeKpi !== 'conversionRate' && amount !== 0 && Number.parseInt(timeUnit) > storeClosingHour) {
            updatedNormalizedStoreClosingHour = Number.parseInt(timeUnit);
          }

          return value;
        });

        setNormalizedStoreOpeningHour(updatedNormalizedStoreOpeningHour);
        setNormalizedStoreClosingHour(updatedNormalizedStoreClosingHour);

        return { ...breakdownItem, values: normalizedItems };
      });
    }, [activeKpi, breakdownItems, currentDate, interval, storeOpeningHour, storeClosingHour]);

    const headerItems = useMemo(() => {
      if (normalizedBreakdownItems.length === 0) return [];

      const filteredValues = getFilteredValues(
        normalizedBreakdownItems[0].values,
        normalizedStoreOpeningHour,
        normalizedStoreClosingHour,
      );

      const timeUnitItems = filteredValues.map((value) => {
        return {
          id: value.timeUnit,
          content: <Typography type="body3">{getTimeUnitText(interval, value.timeUnit)}</Typography>,
        };
      });

      return [
        {
          id: 'storeId',
          content: (
            <Typography type="body3">
              {t(areaResultTranslations.storeId.key, areaResultTranslations.storeId.defaultValue)}
            </Typography>
          ),
        },
        ...timeUnitItems,
      ];
    }, [normalizedBreakdownItems, interval, normalizedStoreOpeningHour, normalizedStoreClosingHour]);
    return (
      <ColumnFlexContainer padding={isTablet ? [4, 4, 12, 4] : [4, 8, 12, 8]}>
        <Button
          data-testid="back-button"
          label={t(commonTranslations.back.key, commonTranslations.back.defaultValue)}
          buttonContentProps={{
            iconOptions: { icon: Icons.ARROW, rotate: 'up', size: 'small' },
            textOptions: {
              whiteSpace: 'nowrap',
            },
          }}
          onClick={backButtonHandler}
          variant="text"
        />

        <Typography type="h3" overflow="inherit">
          {areaName} - ({filteredStoresByArea.length}{' '}
          {t(salesAnalysisTranslation.stores.key, salesAnalysisTranslation.stores.defaultValue)})
        </Typography>

        <RowFlexContainer margin={[0, 0, 2, 0]}>
          <Typography padding={[2, 0, 2, 0]} type="body3" key="results-based-on">
            {t(areaResultTranslations.resultsBasedOn.key, areaResultTranslations.resultsBasedOn.defaultValue)}
          </Typography>
          <Typography key="label" padding={[2, 0]} type="body3">
            {' '}
            {dateLabel}
          </Typography>
        </RowFlexContainer>

        <RowFlexContainer gap={[2]} padding={[0, 0, 4, 0]} flexWrap="wrap">
          {areaBreakdownKpis.map((kpi) => {
            const kpiName = getKpiName(kpi);

            return (
              <Chip
                label={kpiName}
                color={kpi === activeKpi ? 'error' : 'default'}
                onClick={() => setActiveKpi(kpi)}
              />
            );
          })}
        </RowFlexContainer>

        {isLoading && (
          <ColumnFlexContainer flex={1} data-testid="loading-view">
            <Animation
              title={t(
                areaResultTranslations.breakdownView.loading.key,
                areaResultTranslations.breakdownView.loading.defaultValue,
              )}
              animation={Animations.LOADING}
            />
          </ColumnFlexContainer>
        )}

        {!isLoading && isError && (
          <ColumnFlexContainer flex={1} data-testid="error-view">
            <Animation
              title={t(commonTranslations.errorText1.key, commonTranslations.errorText1.defaultValue)}
              subtitle={t(
                areaResultTranslations.breakdownView.error.key,
                areaResultTranslations.breakdownView.error.defaultValue,
              )}
              animation={Animations.SAD}
              button={{
                label: t(commonTranslations.tryAgain.key, commonTranslations.tryAgain.defaultValue),

                onClick: getAreaBreakdownData,
                buttonContentProps: {
                  iconOptions: {
                    size: 'small',
                    icon: Icons.REFRESH,
                    color: 'white',
                  },
                },
              }}
            />
          </ColumnFlexContainer>
        )}

        {!isLoading && !isError && data && (
          <ColumnFlexContainer data-testid="breakdown-view-table-container" flex={1} minHeight={[0]}>
            <Table
              headerItems={headerItems}
              items={normalizedBreakdownItems}
              stickyHeader
              stickyFirstColumn
              getRowItems={(item) => {
                const filteredValues = getFilteredValues(
                  item.values,
                  normalizedStoreOpeningHour,
                  normalizedStoreClosingHour,
                );

                const valueItems = filteredValues.map((value) => {
                  const { timeUnit, amount } = value;
                  return {
                    id: `cell_${item.storeId}_${timeUnit}`,
                    content: <Typography>{getKpiAmountText(activeKpi, amount)}</Typography>,
                  };
                });

                return [
                  { id: `cell_${item.storeId}_id`, content: <Typography type="body3">{item.storeId}</Typography> },
                  ...valueItems,
                ];
              }}
            />
          </ColumnFlexContainer>
        )}
      </ColumnFlexContainer>
    );
  },
);
